import React, {useRef, useState} from 'react';
import styles from './Notification.module.scss';
import Switcher from '@/UIKit/component/switcher/Switcher';
import EditIcon from '@/assets/images/icons/16/pencil.svg';
import ClockIcon from '@/assets/images/icons/16/clock.svg';
import ChartIcon from '@/assets/images/icons/16/chart2.svg'
import DotsIcon from '@/assets/images/icons/16/more-vertical.svg';
import TicketIcon from '@/assets/images/icons/16/ticket.svg';
import Edit from './Edit/Edit'
import Confirm from "@/UIKit/component/confirm/confirm";
import Window from "@/UIKit/component/window/window";
import PeriodModalContent from './Modal/Datetime/Datetime'
import ResendModalContent from './Modal/Resend/Resend'
import Select from "@/UIKit/component/select/Select";
import JSONAPISerializer from "jsonapi-serializer/lib/serializer";
import {useRouteMatch} from "react-router-dom";
import scrollToElement from "@/utils/scrollToElement";
import Toggletip from "@/UIKit/component/toggletip/toggletip";

import {
    handleErrors,
    methodCreateNotification,
    methodCreateTemplate,
    methodGetNotification,
    methodGetUserTemplates,
    methodRemoveTemplate,
    methodResendNotification,
    methodResetNotification,
    methodTestNotification,
    methodUpdateNotification
} from "./Notification.methods";
import NotificationList from "@/views/Admin/Events/Event/Notifications/NotificationList/NotificationList";
import {methodGetStatistics} from "@/views/Admin/Events/Event/Notifications/Notifications.methods";
import {useSelector} from "react-redux";
import {TagsChannel} from "@/views/Admin/Events/Event/Notifications/components/TagChannels";

const Notification = ({data, versions, variables, periods, onChange, tickets, onReset, addNotificationsLimit}) => {
    const is_send_users = useSelector(state => state.admin.event.is_send_users)
    const router = useRouteMatch()
    const {event_id, type} = router.params
    const is_course = type === 'course'
    const [status, setStatus] = useState(false)
    const [version, setVersion] = useState(null)
    const periodModalRef = useRef(null)
    const resendModalRef = useRef(null)
    const notificationRef = useRef(null)
    const [showVersion, setShowVersion] = useState(false)
    const rawChannels = useSelector(state => state.admin.constants.channels)
    const channelsList = rawChannels ? Object.entries(rawChannels).map(([id, label]) => ({
        id: parseInt(id, 10),
        label
    })) : []

    const ref = useRef(document.querySelector('.settings-container'))

    const handleSaveNotification = async (params, reset) => {
        let response
        params = {...params, attributes: {...params.attributes, is_edit: !data.attributes.is_edit}}
        try {
            if (params.hash === null) {
                if (params.attributes.is_preview !== true) {
                    params = {
                        ...params,
                        attributes: {...data.attributes, ...params.attributes},
                        hash: data.hash,
                    }
                }

                response = await methodCreateNotification(params, event_id, is_course)
            } else {
                response = await methodUpdateNotification(params, event_id, is_course)
            }

        } catch (e) {
            return e.response
        }

        if (reset) {
            await onReset()
            scrollToElement(notificationRef, {position: 'center', offset: -70})
            highlightNotification()
        }

        return response
    }

    const handleSaveTemplate = async (params) => {
        let response, serialized

        serialized = new JSONAPISerializer(params.type, {
            keyForAttribute: 'underscore_case',
            attributes: ['event', 'name', 'message', 'subject']
        }).serialize(params);

        try {
            response = await methodCreateTemplate(serialized, event_id)
        } catch (e) {
            return e.response
        }

        return response
    }

    const handleRemoveTemplate = async (params) => {
        let response

        try {
            response = await methodRemoveTemplate(params, event_id)
        } catch (e) {
            showTopMessage('error', 'small', 'Произошла ошибка. Повторите попытку позднее')
        }

        return response
    }

    const handleGetUserTemplates = async () => {
        let response

        try {
            response = await methodGetUserTemplates(event_id)
        } catch (e) {
            showTopMessage('error', 'small', 'Произошла ошибка. Повторите попытку позднее')
        }

        return response.map(x => ({...x, id: Number(x.id)}))
    }

    const handleCancelForm = () => {
        onChange(prev => ({
            ...prev,
            groups: prev.groups.map(g => ({
                ...g,
                list: g.list.map(n => {
                    const updatedVersions = n.versions
                        .filter(x => x.hash)
                        .map(v => ({
                            ...v,
                            attributes: {
                                ...v.attributes,
                                is_disabled: false,
                                is_edit: false,
                                is_preview: false,
                            },
                        }));

                    return updatedVersions.length > 0 ? {...n, versions: updatedVersions} : null;
                }).filter(Boolean),
            })),
        }));
    }

    const handleActive = async ({hash = null, id = null, value = null, ticket_ids = null} = {}) => {
        let response, params, hashNotice = hash || data.hash, val = value !== null ? value : data.attributes.is_active
        params = {
            id: id || data.id || null,
            ticket_ids: ticket_ids,
            attributes: {is_active: !val}
        }

        handleDisableNotification()

        if (!params.id) {
            response = await methodCreateNotification({
                ...params,
                attributes: {...data.attributes, ...params.attributes},
                hash: hashNotice
            }, event_id, is_course);
        } else {
            try {
                response = await methodUpdateNotification(params, event_id, is_course);
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    handleErrors(error.response.data.errors)
                } else {
                    console.error("An error occurred:", error.message);
                }
                handleDisableNotification()
                return
            }
        }

        handleDisableNotification()
        showTopMessage('success', 'small', 'Операция выполнена успешно')

        onChange(prev => ({
            ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                ...g, list: g.list.map((n) => ({
                    ...n,
                    versions: n.versions.map(v => v.hash === hashNotice ? {
                        ...v, id: response ? Number(response.id) : params.id,
                        actions: {...v.actions, ...response.actions},
                        attributes: {...v.attributes, ...params.attributes}
                    } : {...v})
                }))
            } : {...g})
        }))

    }

    const handleEdit = async ({isNewVersion, version} = {}) => {
        let response, params
        handleDisableNotification()
        let hash = version?.hash || data.hash

        if (version) {
            params = {
                ...version,
                hash: hash,
                attributes: {...version.attributes, is_edit: isNewVersion ? false : !version.attributes.is_edit}
            }
        } else {
            params = {
                ...data,
                hash: hash,
                attributes: {...data.attributes, is_edit: isNewVersion ? false : !data.attributes.is_edit}
            }
        }

        if (!data.id) {
            response = await methodCreateNotification(params, event_id, is_course)
        } else {
            response = await methodGetNotification(params, event_id, is_course)
        }

        params = {
            ...params,
            id: Number(response.id),
            attributes: {
                ...params.attributes,
                ...Object.fromEntries(Object.entries(response).filter(x => x[0] !== 'id'))
            }
        }

        let original_id
        if (!isNewVersion && !version) {
            // открыли для редактирования базовую
            original_id = params.attributes.original_id
        } else {
            original_id = version?.attributes?.original_id || data.attributes?.original_id || params.id
        }

        if (isNewVersion) {
            onChange(prev => ({
                ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                    ...g, list: g.list.map((n) => ({
                        ...n,
                        versions: n.versions.flatMap(v =>
                            v.hash === hash
                                ? [
                                    {
                                        ...v, ...params,
                                        attributes: {...v.attributes, ...params.attributes},
                                        info: {...v.info, ...response.info},
                                        actions: {...v.actions, ...response.actions},
                                    },
                                    {
                                        ...v,
                                        id: null,
                                        hash: null,
                                        attributes: {
                                            ...v.attributes, ...params.attributes,
                                            original_id: original_id,
                                            is_edit: true,
                                            is_preview: true
                                        },
                                        info: {...v.info, ...response.info},
                                        actions: {...v.actions, ...response.actions},
                                    }
                                ]
                                : [{
                                    ...v,
                                    attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}
                                }]
                        )

                    }))
                } : {
                    ...g, list: g.list.map((n) => ({
                        ...n,
                        versions: n.versions.map(v => ({
                            ...v,
                            attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}
                        }))
                    }))
                })
            }))
            setShowVersion(true)
        } else {
            // открыте редактирования базовой версии
            let hashNotice = version?.hash || data.hash
            onChange(prev => ({
                ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                    ...g, list: g.list.map((n) => ({
                        ...n,
                        versions: n.versions.map(v => v.hash === hashNotice ? {
                            ...v, ...params,
                            attributes: {...v.attributes, ...params.attributes},
                            info: {...v.info, ...response.info},
                            actions: {...v.actions, ...response.actions},
                        } : {...v, attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}})
                    }))
                } : {
                    ...g, list: g.list.map((n) => ({
                        ...n,
                        versions: n.versions.map(v => ({
                            ...v,
                            attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}
                        }))
                    }))
                })
            }))
        }

    }

    const handleTestMessage = async (form) => {
        let response, fields

        fields = Object.fromEntries(Object.entries(form.fields).map(x => [x[0], x[1].value]))
        response = await methodTestNotification({...fields, id: data.id})

        return response
    }

    const handleSelectAction = (key, version) => {
        key === 'newVersion' ? handleEdit({isNewVersion: true, version: version}) : null
        key === 'resend' ? handleCallWindow({type: key}) : null
        key === 'reset' ? handleConfirm(key, version) : null
        key === 'remove' ? handleConfirm(key, version) : null
    }

    const handleReset = async (version) => {
        let id = version?.id || data.id
        try {
            await methodResetNotification({id: id})
            await onReset()
            showTopMessage('success', 'small', 'Операция выполнена успешно')
            highlightNotification()
        } catch (e) {
            showTopMessage('error', 'small', 'Произошла ошибка. Повторите попытку позднее')
        }
    }

    const handleCallWindow = async ({type = null, version = null} = {}) => {
        await handleCreateNotification({})
        if (type === 'periodVersion') {
            setVersion(version)
            setStatus(type)
        } else {
            setStatus(type)
        }

    }

    const handleSubmitPeriodWindow = async (params) => {

        let response
        let id = version?.id || data.id || null
        let hash = version?.hash || data.hash
        params = {
            id: id,
            hash: hash,
            ticket_ids: params.ticket_ids,
            attributes: {period: params.attributes.period, notify_at: params.attributes.notify_at}
        }

        try {
            params.id ? response = await methodUpdateNotification(params, event_id, is_course) : await methodCreateNotification(params, event_id, is_course)
        } catch (e) {
            return e.response
        }


        onChange(prev => ({
            ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                ...g,
                list: g.list.map(l => ({
                    ...l,
                    versions: l.versions.map(n => n.hash === hash ? {
                        ...n, attributes: {
                            ...n.attributes, notify_at: params.attributes.notify_at, period: params.attributes.period
                        }, info: {...n.info, planned_at: response.info.planned_at}
                    } : {...n})
                }))
            } : {...g})
        }))
        // setVersion(null)
        //   scrollToElement(notificationRef, {position: 'center', offset: -70})
        handleCancelPeriodWindow()
        highlightNotification()
        return response
    }

    const handleSubmitResendWindow = async (params) => {
        let response
        params = {...data, attributes: {period: params.attributes.period, notify_at: params.attributes.notify_at}}

        try {
            response = await methodResendNotification(params)
        } catch (e) {
            return e.response
        }

        await onReset()
        handleCancelResendWindow()
        scrollToElement(notificationRef, {position: 'center', offset: -70})
        highlightNotification()
        return response
    }

    const handleCancelPeriodWindow = () => {
        // setVersion(null)
        periodModalRef.current.handleClose()
    }

    const handleCreateNotification = async (params) => {
        let response

        handleDisableNotification()
        if (!data.id) {
            response = await methodCreateNotification({
                ...params,
                hash: data.hash,
                attributes: {...data.attributes, ...params.attributes}
            }, event_id, is_course)
            onChange(prev => ({
                ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                    ...g,
                    list: g.list.map(n => n.hash === data.hash ? {
                        ...n, id: response ? Number(response.id) : data.id,
                        actions: {...n.actions, ...response.actions},
                        attributes: {...n.attributes, ...params.attributes}
                    } : {...n})
                } : {...g})
            }))
        }
        handleDisableNotification()
        return response
    }

    const handleCancelResendWindow = () => {
        resendModalRef.current.handleClose()
    }

    const handleConfirm = (value, version) => {
        let hash = version?.hash || data.hash
        onChange(prev => ({
            ...prev,
            groups: prev.groups.map(g => g.variables === data.variables ? {
                ...g,
                list: g.list.map(n => ({
                    ...n,
                    versions: n.versions.map(v => v.hash === hash ? {
                        ...v, attributes: {...v.attributes, is_confirm: value}
                    } : {
                        ...v, attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}
                    })
                }))
            } : {
                ...g,
                list: g.list.map(n => ({
                    ...n,
                    versions: n.versions.map(v => ({
                        ...v, attributes: {...v.attributes, is_disabled: !v.attributes.is_disabled}
                    }))
                }))
            })
        }))

    }

    const handleDisableNotification = () => {
        onChange(prev => ({
            ...prev, groups: prev.groups.map(g => g.variables === data.variables ? {
                ...g, list: g.list.map(n => n.hash === data.hash ? {
                    ...n,
                    attributes: {...n.attributes, is_disabled: !n.attributes.is_disabled}
                } : {...n})
            } : {...g})
        }))
    }

    const generateAvailableActions = ({type = null} = {}) => {
        let obj = {}
        data.actions.resend ? obj.resend = 'Отправить повторно' : null
        addNotificationsLimit > 0 && data.actions.version ? obj.newVersion = 'Создать версию' : null
        data.actions.reset ? obj.reset = type === 'versions' ? 'Удалить' : 'Вернуть к базовому шаблону' : null
        data.actions.delete ? obj.remove = 'Удалить шаблон' : null

        return obj
    }

    const generateStatisticText = () => {
        const {count_errors, count_sent, count_viewed, count_unsubscribed} = data.info
        return [
            count_sent ? ['отправлено', count_sent].join(': ') : null,
            count_viewed ? ['просмотрено', count_viewed].join(': ') : null,
            count_unsubscribed ? ['отписано', count_unsubscribed].join(': ') : null,
            count_errors ? ['ошибок', count_errors].join(': ') : null,
        ].filter(x => x ?? x).join(', ')
    }

    const checkGenerateStatistic = () => {
        let {count_sent, count_viewed, count_errors, count_unsubscribed} = data.info
        return count_sent || count_viewed || count_errors || count_unsubscribed
    }

    const highlightNotification = () => {
        setTimeout(() => {
            notificationRef.current ? notificationRef.current.classList.add(styles.highlight) : null
        }, 350)
        setTimeout(() => {
            notificationRef.current ? notificationRef.current.classList.remove(styles.highlight) : null
        }, 500)
    }
    const [statistics, setStatistics] = useState({
        visible: false,
        send: null,
        viewed: null,
        errors: null,
    })

    const handleStatistics = async (item) => {
        try {
            const {
                data: {
                    data: {
                        sent = 0,
                        viewed = 0,
                        errors = 0
                    } = {}
                } = {}
            } = await methodGetStatistics(item, event_id, is_course);
            setStatistics({
                visible: true,
                send: {label: 'Отправлено', value: sent || 0},
                viewed: {label: 'Просмотрено', value: viewed || 0},
                errors: {label: 'Ошибок', value: errors || 0}
            });
        } catch (e) {
            console.error(e);
        }
    };


    const parseToggle = (data) => {
        if (!data.visible) return null
        const filteredData = Object.entries(data).filter(([key, val]) => key !== 'visible' && val !== null);
        return <div>
            {filteredData.map(([key, val]) => (
                <div key={key}>{`${val.label}: ${val.value}`}</div>
            ))}
        </div>
    }

    const getPeriodData = () => {
        if (status === 'period') return data
        if (status === 'periodVersion') return version
    }

    const ticketsFilter = ({tickets, type}) => {
        if (!type) return tickets // если тип 0 то для всех билетов
        return tickets.filter(t => t.type & type)
    }

    return (<>
            {data &&
                <>
                    <Window
                        ref={periodModalRef}
                        status={(status === 'period' || status === 'periodVersion')}
                        setStatus={setStatus}
                        dismissible={true}
                        esc={true}
                        selector={styles.modal}
                        content={<PeriodModalContent
                            data={getPeriodData()}
                            periods={periods}
                            onSubmit={handleSubmitPeriodWindow}
                            onCancel={handleCancelPeriodWindow}
                        />}
                    />
                    <Window
                        ref={resendModalRef}
                        status={status === 'resend'}
                        setStatus={setStatus}
                        dismissible={true}
                        esc={true}
                        selector={styles.resend}
                        content={<ResendModalContent
                            data={data}
                            onSubmit={handleSubmitResendWindow}
                            onCancel={handleCancelResendWindow}
                        />}
                    />
                </>}

            {versions.length === 1 ?
                data && <div className={`${styles.container} ${data.attributes.is_edit ? styles.edit : ''}`}
                             ref={notificationRef}>
                    {data.attributes.is_confirm === 'reset' ? <Confirm
                        type={'danger'}
                        message={`Вернуть к базовым настройкам?`}
                        onCancelText={'Отмена'}
                        onSubmitText={'Вернуть'}
                        direction={'row'}
                        onCancel={handleConfirm}
                        onSubmit={handleReset}
                    /> : null}

                    {data.attributes.is_confirm === 'remove' ? <Confirm
                        type={'danger'}
                        message={`Удалить уведомление?`}
                        onCancelText={'Отмена'}
                        onSubmitText={'Удалить'}
                        direction={'row'}
                        onCancel={handleConfirm}
                        onSubmit={handleReset}
                    /> : null}

                    {data.attributes.is_edit ? <Edit
                        data={data}
                        addNotificationsLimit={addNotificationsLimit}
                        tickets={ticketsFilter({tickets: tickets, type: data.attributes.ticket_types})}
                        variables={variables}
                        onSaveNotification={handleSaveNotification}
                        onSaveTemplate={handleSaveTemplate}
                        onRemoveTemplate={handleRemoveTemplate}
                        onGetTemplates={handleGetUserTemplates}
                        onCancel={handleCancelForm}
                        onTest={handleTestMessage}
                        channelsList={channelsList}
                    /> : <>
                        <div className={styles.name}>
                            {data.attributes.name}
                            <div className={styles.tagsChannel}>
                                <TagsChannel channelsList={channelsList} mask={data.attributes.channels}/>
                            </div>
                        </div>
                        <div className={styles.type}>
                            <span>тип билета: </span>
                            {data.info.ticket_types}
                        </div>
                        <div className={styles.statistic}>
                            {data.info.planned_at ? <p>
                                {data.info.planned_at}
                            </p> : null}
                            {checkGenerateStatistic() ? <p>
                                {generateStatisticText()}
                            </p> : null}
                        </div>
                        <div className={styles.actions}>
                            {data.actions.notify ?
                                <button
                                    className={`a-btn a-btn_size_small a-btn_quare a-btn_type_secondary-ghost ${styles.buttonN}`}
                                    title={'Настроить время отправки'}
                                    disabled={data.attributes.is_disabled}
                                    onClick={() => handleCallWindow({type: 'period'})}>
                                    <ClockIcon/>
                                </button>
                                : null}
                            <div style={{height: "max-content"}}>
                                <Toggletip content={parseToggle(statistics)}>
                                    <button
                                        className={`a-btn a-btn_size_small a-btn_quare a-btn_type_secondary-ghost ${styles.buttonN}`}
                                        disabled={data.attributes.is_disabled}
                                        title={'Статистика'}
                                        onClick={() => handleStatistics(data)}>
                                        <ChartIcon/>
                                    </button>
                                </Toggletip>
                            </div>
                            <button
                                className={`a-btn a-btn_size_small a-btn_quare a-btn_type_secondary-ghost ${styles.buttonN}`}
                                title={'Редактировать'}
                                disabled={data.attributes.is_disabled || !data.actions.edit}
                                onClick={() => handleEdit()}>
                                <EditIcon/>
                            </button>
                            <Select
                                data={generateAvailableActions()}
                                parentRef={ref}
                                disabled={!Object.keys(generateAvailableActions()).length || data.attributes.is_disabled}
                                onChange={handleSelectAction}
                            >
                                <div
                                    className={`a-btn a-btn_size_small a-btn_quare a-btn_type_secondary-ghost ${styles.buttonN}`}>
                                    <DotsIcon/>
                                </div>
                            </Select>
                        </div>
                        <div className={styles.switchers}>
                            <Switcher
                                id={`notification-switcher__${data.hash}`}
                                size={'small'}
                                parent={document.querySelector('main')}
                                value={null}
                                checked={!is_send_users ? false : data.attributes.is_active}
                                disabled={!is_send_users ? true : data.attributes.is_disabled || !data.actions.disable}
                                onChange={handleActive}
                                error={null}
                            />
                        </div>
                    </>}
                </div>
                :
                <>
                    <NotificationList
                        data={versions[0]}
                        versions={versions}
                        onChange={onChange}
                        variables={variables}
                        tickets={tickets}
                        handleSaveNotification={handleSaveNotification}
                        handleSaveTemplate={handleSaveTemplate}
                        handleRemoveTemplate={handleRemoveTemplate}
                        handleGetUserTemplates={handleGetUserTemplates}
                        handleCancelForm={handleCancelForm}
                        handleTestMessage={handleTestMessage}
                        handleCallWindow={handleCallWindow}
                        generateAvailableActions={generateAvailableActions}
                        handleSelectAction={handleSelectAction}
                        handleActive={handleActive}
                        statistics={statistics}
                        parseToggle={parseToggle}
                        handleStatistics={handleStatistics}
                        handleEdit={handleEdit}
                        handleConfirm={handleConfirm}
                        handleReset={handleReset}
                        setShowVersion={setShowVersion}
                        showVersion={showVersion}
                        ticketsFilter={ticketsFilter}
                        channelsList={channelsList}
                    />
                </>
            }

        </>
    )
        ;
};

export default Notification;
